import { FullscreenLoadingSpinner } from '@components/Loading/FullscreenLoadingSpinner';
import { Toast } from '@components/Toast';
import { styled, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductLibraryQuery } from '@partful/pages/product-library/hooks/useProductLibraryQuery';
import { isJobInProgress } from '@partful/pages/product-library/ui/product-list/jobFiltering';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { Job } from '@providers/job/types';
import { useTenant } from '@providers/Tenant';
import {
  ColourMode,
  Configuration,
  getConfiguration,
  PostProcessing,
  productInventoryEndpoints,
} from '@services/productInventory';
import { useDecodedParams } from 'src/hooks/useDecodedParams';

type ConfigurationValue =
  | (Configuration & {
      thumbnailUrl?: string;
      isUpdating?: boolean;
      isUpdatingPartsCategories?: boolean;
      isUpdatingSalesBOM?: boolean;
      isUpdatingKits?: boolean;
    })
  | undefined;

export const ConfigurationContext = createContext({} as ConfigurationValue);

export function ConfigurationProvider({ children }: any) {
  const { accountId } = useTenant();
  const { configurations: productList } = useProductLibraryQuery();
  const { t } = useTranslation('manageProduct');

  const { productId } = useDecodedParams<{ productId: string }>();
  const { configurationPID } = useQueryParams('configurationPID');
  const thumbnailUrl = productList?.find((product) => product.configurationExternalID === productId)?.thumbnailUrl;

  const {
    data: currentConfiguration,
    isLoading: isLoadingCurrentConfiguration,
    isError: isErrorGetCurrentConfiguration,
  } = useQuery(['getConfiguration'], () => getConfiguration(configurationPID, accountId), { refetchOnMount: true });

  const { data: jobs, isLoading: isLoadingJobs } = useProductInventoryQuery<Job[]>(
    productInventoryEndpoints.contentProcessing.root,
    {
      account: accountId,
      configurationPID,
    }
  );

  const inProgressJobType = jobs?.find((job) => isJobInProgress(job) && job.status !== 'IN_PROGRESS_PREVIEW');
  const isUpdating = Boolean(inProgressJobType);
  const isUpdatingPartsCategories = inProgressJobType?.jobType === 'PartCategories';
  const isUpdatingSalesBOM = inProgressJobType?.jobType === 'SalesBOM';
  const isUpdatingKits = inProgressJobType?.jobType === 'Kits';

  const value: ConfigurationValue = useMemo(
    () =>
      currentConfiguration
        ? {
            ...currentConfiguration,
            colourMode: currentConfiguration?.colourMode ?? ColourMode.COLOUR,
            postProcessing: currentConfiguration?.postProcessing ?? PostProcessing.ADVANCED,
            isUpdating,
            isUpdatingPartsCategories,
            isUpdatingSalesBOM,
            isUpdatingKits,
            thumbnailUrl,
          }
        : undefined,
    [currentConfiguration, isUpdating, isUpdatingPartsCategories, isUpdatingSalesBOM, isUpdatingKits, thumbnailUrl]
  );
  if (isLoadingCurrentConfiguration || isLoadingJobs) {
    return <FullscreenLoadingSpinner />;
  }

  /* istanbul ignore next */
  if (isErrorGetCurrentConfiguration) {
    return (
      <>
        <ErrorPage>{t('productOverview.errorGettingConfiguration')}</ErrorPage>
        <Toast
          dependency={isErrorGetCurrentConfiguration}
          severity="error"
          title="Error getting the selected configuration"
        />
      </>
    );
  }

  return <ConfigurationContext.Provider value={value}>{children}</ConfigurationContext.Provider>;
}

export const useConfiguration = () => {
  const context = useContext(ConfigurationContext);
  /* istanbul ignore next */
  if (context === undefined) {
    throw new Error('`useConfiguration` hook must be used within a `ConfigurationProvider` component');
  }
  return context;
};

const ErrorPage = styled(Typography)`
  margin: auto;
`;
